<template>
  <v-dialog v-model="dialog" max-width="680" @click:outside="cancel" :persistent="isLoading || result != null">
    <!-- 確認ダイアログ -->
    <v-card v-if="result == null" data-testid="bulk-delete-dialog">
      <v-card-title>
        <h3><strong>{{ $t('nyuukaPlanBulkDeleteDialog.label.title') }}</strong></h3>
      </v-card-title>
      <v-card-text>
        {{ $t('nyuukaPlanBulkDeleteDialog.message.confirmDelete', [denpyoNumberList.length]) }}
        <!-- 削除対象の伝票番号。件数が多い場合はスクロールさせる -->
        <v-table density="compact" fixed-header height="240" class="mt-2" data-testid="bulk-delete-confirm-list">
          <thead>
            <tr>
              <th>{{ $t('nyuukaPlanBulkDeleteDialog.label.denpyoNumber') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="denpyoNumber in denpyoNumberList" :key="denpyoNumber">
              <td class="text-no-wrap">{{ denpyoNumber }}</td>
            </tr>
          </tbody>
        </v-table>
        <v-text-field data-testid="bulk-delete-staff" density="compact" variant="outlined" hide-details="auto"
          v-model="staffCode" class="mt-4" :label="$t('nyuukaPlanBulkDeleteDialog.label.staffCode')"
          :counter="staffCodeLength" :maxLength="staffCodeLength" :disabled="isLoading" :rules="[rules.checkStaffCode]" />
        <div v-if="errorMessage" class="mt-4" style="color: red" data-testid="bulk-delete-error">
          {{ errorMessage }}
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn data-testid="bulk-delete-cancel" text @click="cancel" :disabled="isLoading">{{ $t('common.action.cancel') }}</v-btn>
        <v-btn data-testid="bulk-delete-ok" color="error" text @click="deleteBulk" :loading="isLoading"
          :disabled="invalidInput || isLoading">
          {{ $t('common.action.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- 結果ダイアログ -->
    <v-card v-else data-testid="bulk-delete-result">
      <v-card-title>
        <h3><strong>{{ $t('nyuukaPlanBulkDeleteDialog.label.resultTitle') }}</strong></h3>
      </v-card-title>
      <v-card-text>
        <div data-testid="bulk-delete-result-deleted">
          {{ $t('nyuukaPlanBulkDeleteDialog.message.deleted', [deletedCount]) }}
        </div>
        <template v-if="skippedList.length > 0">
          <div class="mt-4" data-testid="bulk-delete-result-skipped">
            {{ $t('nyuukaPlanBulkDeleteDialog.message.skipped', [skippedList.length]) }}
          </div>
          <!-- 件数が多い場合はスクロールさせる -->
          <v-table density="compact" fixed-header height="280" class="mt-2" data-testid="bulk-delete-result-skipped-list">
            <thead>
              <tr>
                <th>{{ $t('nyuukaPlanBulkDeleteDialog.label.denpyoNumber') }}</th>
                <th>{{ $t('nyuukaPlanBulkDeleteDialog.label.reason') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in skippedList" :key="item.denpyoNumber">
                <td class="text-no-wrap">{{ item.denpyoNumber }}</td>
                <td>{{ item.message || item.reason }}</td>
              </tr>
            </tbody>
          </v-table>
        </template>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn data-testid="bulk-delete-result-close" color="indigo darken-1" text @click="done">
          {{ $t('common.action.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/apis/staff";
import constant from "@/utils/constant";

export default {
  props: {
    // 削除対象の伝票番号リスト
    denpyoNumberList: {
      type: Array,
      required: true,
    },
    // 結果ダイアログを閉じたとき（一覧の再検索・選択解除は呼び出し元で行う）
    handleDone: {
      type: Function,
      required: true,
    },
    // 確認ダイアログのキャンセル
    handleCancel: {
      type: Function,
      required: true,
    }
  },
  computed: {
    // 担当者コードは任意入力。入力されている場合のみ、半角英数字かつ桁数を確認する
    invalidInput() {
      if (!this.isValidStaffCode(this.staffCode)) return true;
      if (this.staffCode.length > this.staffCodeLength) return true;
      return false;
    },
    deletedCount() {
      return this.result?.deletedCount ?? this.result?.deletedList?.length ?? 0;
    },
    skippedList() {
      return this.result?.skippedList ?? [];
    },
  },
  data() {
    return {
      dialog: true,
      isLoading: false,
      errorMessage: "",
      // 削除結果（APIのresults）。nullの間は確認ダイアログを表示する
      result: null,
      staffCode: "",
      staffCodeLength: constant.STAFF_CODE_MAX_LENGTH,
      rules: {
        checkStaffCode: v => {
          return this.isValidStaffCode(v) || this.$t('nyuukaPlanBulkDeleteDialog.validation.halfAlnum');
        },
      },
    };
  },
  methods: {
    // 未入力は許容する（任意項目のため）
    isValidStaffCode(v) {
      return /^[0-9A-Za-z]*$/.test(v ?? "");
    },
    cancel() {
      if (this.isLoading || this.result != null) return;
      this.handleCancel();
    },
    done() {
      this.handleDone();
    },
    deleteBulk() {
      // 連打による二重送信を防ぐ
      if (this.isLoading || this.invalidInput) return;
      this.isLoading = true;
      this.errorMessage = "";
      api.post("/nyuka/plan/delete-bulk", {
        denpyoNumberList: Array.from(new Set(this.denpyoNumberList)),
        staffId: this.staffCode,
      })
        .then((response) => {
          if (response.data?.status == 0) {
            this.result = response.data.results ?? {};
          } else {
            this.errorMessage = response.data?.errorMessage || this.$t('nyuukaPlanBulkDeleteDialog.message.deleteFailed');
          }
        })
        .catch((error) => {
          console.error("Bulk delete nyuuka plan error:", error);
          this.errorMessage = this.$t('common.message.errorAndRetry');
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>
